import { FC, useMemo, useState } from 'react'

import moment from 'moment'
import classNames from 'classnames'
import { useGetCalendarDataDatesQuery } from 'src/reactQuery/useGetCalendarDataDatesQuery'
import { CalendarContext } from 'src/utils/calendarContext'

import styles from './customCalendar.module.scss'

// The data-dot context (source + module ids) comes in as the shared
// CalendarContext, so every host describes "whose days" the same way.
interface CustomCalendarProps extends CalendarContext {
  className?: string
  /** Off skips the per-month "has data" request entirely. */
  enableDataDots?: boolean
  initialEnd?: moment.Moment | null
  initialStart?: moment.Moment | null
  maxDate?: moment.Moment | null
  minDate?: moment.Moment | null
  /** >1 renders consecutive months side by side, each under its own title. */
  monthsToShow?: number
  /** Replaces the month name between the arrows; months then title themselves. */
  navLabel?: string
  onApply: (start: moment.Moment, end: moment.Moment) => void
  onClose: () => void
  onReset?: () => void
  /**
   * Fired as the range is picked, before any Apply. For hosts that own the
   * footer themselves — a wizard step whose Next button is the Apply — and so
   * pass showFooter={false}.
   */
  onSelect?: (start: moment.Moment, end: moment.Moment | null) => void
  selectionMode?: 'range' | 'single'
  /** Off hides Reset/Apply entirely; pair it with onSelect. */
  showFooter?: boolean
  /** Prints the chosen range beside Reset/Apply. */
  showRangeLabel?: boolean
  weekStart?: 'monday' | 'sunday'
}

const WEEKDAY_LABELS: Record<'monday' | 'sunday', string[]> = {
  monday: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
  sunday: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
}
const DAYS_IN_WEEK = 7
// Every month is drawn as the same 6-week grid (the most any month can span),
// padded with the neighbouring months' days. A 5-row minimum made 6-week
// months one row taller, so the calendar jumped in height when paging.
const WEEK_ROWS = 6

export interface CalendarDay {
  date: moment.Moment
  isCurrentMonth: boolean
}

/** The fixed 6×7 grid of days shown for `monthDate`. */
export const buildMonthDays = (
  monthDate: moment.Moment,
  weekStart: 'monday' | 'sunday'
): CalendarDay[] => {
  const startOfMonth = monthDate.clone().startOf('month')
  const leadingDays =
    weekStart === 'sunday' ? startOfMonth.day() : (startOfMonth.day() + 6) % 7
  const gridStart = startOfMonth.clone().subtract(leadingDays, 'days')

  return Array.from({ length: WEEK_ROWS * DAYS_IN_WEEK }, (_, index) => {
    const date = gridStart.clone().add(index, 'days')
    return { date, isCurrentMonth: date.isSame(startOfMonth, 'month') }
  })
}
const RANGE_LABEL_FORMAT = 'MMM D, YYYY'

export const CustomCalendar: FC<CustomCalendarProps> = ({
  analysisId,
  campaignId,
  className,
  enableDataDots = true,
  facilityId,
  initialEnd,
  initialStart,
  keyword,
  maxDate,
  minDate,
  monthsToShow = 1,
  navLabel,
  onApply,
  onClose,
  onSelect,
  onReset,
  pageId,
  platformId,
  selectionMode = 'range',
  showFooter = true,
  showRangeLabel = false,
  source,
  weekStart = 'monday',
}) => {
  const initialViewDate = initialStart ?? initialEnd

  const [currentViewDate, setCurrentViewDate] = useState(
    initialViewDate
      ? initialViewDate.clone().startOf('month')
      : moment().startOf('month')
  )
  const [startDate, setStartDate] = useState<moment.Moment | null>(
    initialStart ?? initialEnd ?? null
  )
  const [endDate, setEndDate] = useState<moment.Moment | null>(
    selectionMode === 'single'
      ? initialStart ?? initialEnd ?? null
      : initialEnd ?? null
  )
  const [hoveredDate, setHoveredDate] = useState<moment.Moment | null>(null)

  const displayedMonth = currentViewDate.format('YYYY-MM')
  const secondMonth = currentViewDate.clone().add(1, 'month').format('YYYY-MM')

  const calendar: CalendarContext = {
    analysisId,
    campaignId,
    facilityId,
    keyword,
    pageId,
    platformId,
    source,
  }

  const { data: dataDates, isFetching: isLoadingDots } =
    useGetCalendarDataDatesQuery({
      ...calendar,
      // The hook is gated on `month`, so dropping it disables the request
      // without breaking the rules of hooks.
      month: enableDataDots ? displayedMonth : undefined,
    })

  // The dots are asked for per month, so a two-month calendar has to ask twice
  // — otherwise the right-hand month is always bare and reads as "no data here",
  // which is a different claim from "not asked".
  const { data: secondMonthDates, isFetching: isLoadingSecondDots } =
    useGetCalendarDataDatesQuery({
      ...calendar,
      month: enableDataDots && monthsToShow > 1 ? secondMonth : undefined,
    })

  const isLoadingDotsAny = isLoadingDots || isLoadingSecondDots

  const dataDatesSet = useMemo(
    () =>
      new Set(
        isLoadingDotsAny
          ? []
          : [...(dataDates ?? []), ...(secondMonthDates ?? [])]
      ),
    [dataDates, isLoadingDotsAny, secondMonthDates]
  )

  // Announces every range change to a host that owns its own footer. The
  // calendar keeps its internal state either way, so nothing changes for the
  // callers that Apply.
  const announce = (start: moment.Moment | null, end: moment.Moment | null) => {
    if (start) {
      onSelect?.(start, end)
    }
  }

  const handleDayClick = (date: moment.Moment) => {
    if (
      (minDate && date.isBefore(minDate, 'day')) ||
      (maxDate && date.isAfter(maxDate, 'day'))
    ) {
      return
    }

    if (
      selectionMode === 'single' &&
      startDate &&
      endDate &&
      startDate.isSame(date, 'day') &&
      endDate.isSame(date, 'day')
    ) {
      setStartDate(null)
      setEndDate(null)
      return
    }

    if (selectionMode === 'single') {
      setStartDate(date)
      setEndDate(date)
      return
    }

    if (startDate && !endDate && startDate.isSame(date, 'day')) {
      setStartDate(null)
      setEndDate(null)
      return
    }

    if (!startDate || (startDate && endDate)) {
      setStartDate(date)
      setEndDate(null)
      announce(date, null)
    } else if (startDate && !endDate) {
      if (date.isBefore(startDate)) {
        setStartDate(date)
        setEndDate(startDate)
        announce(date, startDate)
      } else {
        setEndDate(date)
        announce(startDate, date)
      }
    }
  }

  const handlePrevMonth = () => {
    setCurrentViewDate((prev) => prev.clone().subtract(1, 'month'))
  }

  const handleNextMonth = () => {
    setCurrentViewDate((prev) => prev.clone().add(1, 'month'))
  }

  const handleReset = () => {
    setStartDate(null)
    setEndDate(null)
    onReset?.()
  }

  const handleApply = () => {
    if (startDate && endDate) {
      onApply(startDate, endDate)
      onClose()
    } else if (startDate) {
      onApply(startDate, startDate)
      onClose()
    }
  }

  const months = useMemo(() => {
    const buildMonth = (monthDate: moment.Moment) => ({
      days: buildMonthDays(monthDate, weekStart),
      monthDate,
    })

    return Array.from({ length: Math.max(1, monthsToShow) }, (_, offset) =>
      buildMonth(currentViewDate.clone().add(offset, 'month'))
    )
  }, [currentViewDate, monthsToShow, weekStart])

  return (
    <div className={classNames(styles.customCalendarContainer, className)}>
      <div className={styles.calendarContent}>
        <div className={styles.header}>
          <button
            className={styles.navButton}
            onClick={handlePrevMonth}
            type="button"
          >
            <svg
              fill="none"
              height="24"
              viewBox="0 0 24 24"
              width="24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 18L9 12L15 6"
                stroke="#86949D"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </button>
          <span className={navLabel ? styles.navCaption : styles.monthTitle}>
            {navLabel ?? currentViewDate.format('MMMM YYYY')}
          </span>
          <button
            className={styles.navButton}
            onClick={handleNextMonth}
            type="button"
          >
            <svg
              fill="none"
              height="24"
              viewBox="0 0 24 24"
              width="24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 18L15 12L9 6"
                stroke="#86949D"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </button>
        </div>

        <div className={styles.monthsRow}>
          {months.map((month) => (
            <div
              className={styles.month}
              key={month.monthDate.format('YYYY-MM')}
            >
              {navLabel && (
                <span className={styles.perMonthTitle}>
                  {month.monthDate.format('MMMM YYYY')}
                </span>
              )}

              <div className={styles.daysGrid}>
                {WEEKDAY_LABELS[weekStart].map((day) => (
                  <div className={styles.dayLabel} key={day}>
                    {day}
                  </div>
                ))}
                {month.days.map((dayItem, index) => {
                  const { date } = dayItem
                  const isRangeStart = !!startDate?.isSame(date, 'day')
                  const isRangeEnd = !!endDate?.isSame(date, 'day')
                  const isSelected = isRangeStart || isRangeEnd
                  const isDisabled =
                    (minDate && date.isBefore(minDate, 'day')) ||
                    (maxDate && date.isAfter(maxDate, 'day'))
                  const isInRange =
                    selectionMode === 'range' &&
                    startDate &&
                    endDate &&
                    date.isAfter(startDate, 'day') &&
                    date.isBefore(endDate, 'day')
                  const isToday = date.isSame(moment(), 'day')
                  const dateStr = date.format('YYYY-MM-DD')
                  const hasData = !isLoadingDotsAny && dataDatesSet.has(dateStr)

                  // Hover preview: show range highlight when start is
                  // picked but end is not yet confirmed
                  const isHoverPreview =
                    selectionMode === 'range' &&
                    startDate &&
                    !endDate &&
                    hoveredDate &&
                    !startDate.isSame(hoveredDate, 'day') &&
                    ((hoveredDate.isAfter(startDate, 'day') &&
                      date.isAfter(startDate, 'day') &&
                      date.isBefore(hoveredDate, 'day')) ||
                      (hoveredDate.isBefore(startDate, 'day') &&
                        date.isBefore(startDate, 'day') &&
                        date.isAfter(hoveredDate, 'day')))
                  const isHoverEnd =
                    selectionMode === 'range' &&
                    startDate &&
                    !endDate &&
                    hoveredDate &&
                    !startDate.isSame(hoveredDate, 'day') &&
                    hoveredDate.isSame(date, 'day')

                  return (
                    <div
                      className={classNames(styles.dayCell, {
                        [styles.hoverEnd]: isHoverEnd,
                        [styles.hoverPreview]: isHoverPreview,
                        [styles.inRange]: isInRange,
                        [styles.outsideMonth]: !dayItem.isCurrentMonth,
                        [styles.disabled]: isDisabled,
                        [styles.rangeEnd]:
                          selectionMode === 'range' && isRangeEnd && startDate,
                        [styles.rangeStart]:
                          selectionMode === 'range' && isRangeStart && endDate,
                        [styles.selected]:
                          selectionMode === 'single'
                            ? isSelected
                            : isSelected && !endDate,
                        [styles.today]: isToday,
                      })}
                      key={index}
                      onClick={() => handleDayClick(date)}
                      onMouseEnter={() => !isDisabled && setHoveredDate(date)}
                      onMouseLeave={() => setHoveredDate(null)}
                    >
                      {date.date()}
                      {hasData && <div className={styles.dataDot} />}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
          {/* Overlaid (position: absolute) in the gap under the grid, so the
              loading line never adds height to the calendar while it runs. */}
          {isLoadingDotsAny && (
            <div aria-hidden className={styles.dotsLoader} />
          )}
        </div>

        {showFooter && (
          <div
            className={classNames(styles.footer, {
              [styles.footerRuled]: showRangeLabel,
            })}
          >
            {showRangeLabel && (
              <span className={styles.rangeLabel}>
                {startDate &&
                  `${startDate.format(RANGE_LABEL_FORMAT)} – ${(
                    endDate ?? startDate
                  ).format(RANGE_LABEL_FORMAT)}`}
              </span>
            )}
            <button
              className={styles.resetButton}
              onClick={handleReset}
              type="button"
            >
              Reset
            </button>
            <button
              className={styles.applyButton}
              disabled={!startDate}
              onClick={handleApply}
              type="button"
            >
              Apply
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
